import { ApiError } from './api';

/**
 * Разбор `details` из ответа API в подписи под конкретными полями формы.
 *
 * Backend отдаёт два вида адресных ошибок: список `issues` от Zod-валидации и
 * одиночное `field` у доменных проверок вроде срока в прошлом. Оба приводятся
 * к `{ поле: сообщение }`, чтобы форма подсветила именно то место, где
 * пользователь ошибся.
 */
interface RawIssue {
  field?: unknown;
  code?: unknown;
  message?: unknown;
}

/**
 * Встроенные проверки Zod пишут по-английски, а собственные — сразу по-русски.
 * Кириллица в сообщении и есть признак того, что текст писали для пользователя.
 */
const CODE_MESSAGES: Record<string, string> = {
  invalid_type: 'Заполните поле',
  too_small: 'Слишком короткое или маленькое значение',
  too_big: 'Слишком длинное или большое значение',
  invalid_format: 'Неверный формат',
  invalid_value: 'Недопустимое значение',
  invalid_union: 'Неверный формат',
  unrecognized_keys: 'Лишнее поле в запросе',
  not_multiple_of: 'Недопустимое значение',
};

const FALLBACK_MESSAGE = 'Проверьте значение';

export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || !error.details) {
    return {};
  }

  const errors: Record<string, string> = {};
  const { issues, field } = error.details as { issues?: unknown; field?: unknown };

  if (typeof field === 'string') {
    errors[field] = error.message;
  }

  if (Array.isArray(issues)) {
    for (const issue of issues as RawIssue[]) {
      if (typeof issue?.field !== 'string' || issue.field in errors) {
        continue;
      }

      errors[issue.field] = issueMessage(issue);
    }
  }

  return errors;
}

/** Названия полей с ошибками — для случаев, когда подписать поле негде. */
export function errorFieldLabels(error: unknown, labels: Record<string, string>): string[] {
  return Object.keys(fieldErrors(error))
    .map((field) => labels[field])
    .filter((label): label is string => label !== undefined);
}

function issueMessage(issue: RawIssue): string {
  const message = typeof issue.message === 'string' ? issue.message : '';

  if (message && (issue.code === 'custom' || /[а-яё]/i.test(message))) {
    return message;
  }

  return (
    (typeof issue.code === 'string' ? CODE_MESSAGES[issue.code] : undefined) ?? FALLBACK_MESSAGE
  );
}
